import { TrendingUp, TrendingDown, Minus, Mail, Phone, Globe, Download, LayoutDashboard } from "lucide-react";
import { Button, Card, CardBody, Col, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { IDashboardFilterData, } from "../../core/_models";
import SimpleLoader from "../../apps/components/SimpleLoader";
import {
    DEPARTMENTS,
    calculatePerformance,
    getTrendIndicator,
    getStatusColorFromText,
    type DepartmentKpi,
    type KpiItem,
} from "./kpiDepartmentsData";
import SectionHeader from "./SectionHeader";

interface Props {
    setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
    filters: IDashboardFilterData
    refreshData: boolean;
    setRefreshData: React.Dispatch<React.SetStateAction<boolean>>;
}

const performanceData = [
    {
        value: "78%",
        label: "Excellent Performance",
        color: "text-success"
    },
    {
        value: "19%",
        label: "Good Performance",
        color: "text-primary"
    },
    {
        value: "33/36",
        label: "On Target KPIs",
        color: "text-info"
    },
    {
        value: "31/36",
        label: "Positive Trends",
        color: "text-success"
    }
];

const references = [
    "Western Cape Government Department of the Premier, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Provincial Treasury, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Police Oversight and Community Safety, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Education Department, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Health and Wellness, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Department of Social Development, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Mobility Department, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Department of Infrastructure, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Department of Economic Development and Tourism, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Department of Environmental Affairs and Development Planning, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Department of Local Government, 2024. Annual Performance Plan 2024/25. Western Cape Government.",
    "Western Cape Government Department of Cultural Affairs and Sport, 2024. Annual Performance Plan 2024/25. Western Cape Government."
];

// Reusable KPI Card Component
const KPICard: React.FC<{ kpi: KpiItem, bgColor: string, hovered: boolean }> = ({ kpi, bgColor, hovered }) => {

    const [count, setCount] = useState(0);

    useEffect(() => {
        let start = 0;
        let end = kpi.value;
        let duration = 3000; // animation duration (ms)
        let incrementTime = 20;
        let step = Math.ceil(end / (duration / incrementTime));

        if (hovered) {
            const timer = setInterval(() => {
                start += step;
                if (start >= end) {
                    start = end;
                    clearInterval(timer);
                }
                setCount(start);
            }, incrementTime);

            return () => clearInterval(timer);
        } else {
            setCount(0); // reset when not hovered
        }
    }, [hovered, kpi.value]);

    const performance = calculatePerformance(kpi.value, kpi.target);
    const trend = getTrendIndicator(kpi.value, kpi.target);
    const statusColor = getStatusColorFromText(kpi.status);

    return (
        <Card>
            <CardBody className="w-100 p-3 h-100 bg-opacity-4 cursor-pointer">

                <div className="d-flex align-items-center gap-2 justify-content-between">
                    <small>{kpi.name}</small>
                    {kpi.tranding === 'up' ? <TrendingUp className="text-success" size={18} /> : kpi.tranding === 'down' ? <TrendingDown className="text-danger" size={18} /> : <Minus className="text-gray-500" size={18} />}
                </div>
                <div className="d-flex align-items-center gap-2 justify-content-between mt-3">
                    <div>
                        <span className="fw-bold fs-3 me-1">
                            {hovered ? count : kpi.value}
                        </span>
                        <small className="text-muted">{kpi.unit}</small>
                    </div>
                    <small className="text-muted">Target: {kpi.target} {kpi.unit}</small>
                </div>
                <div
                    className="progress progress-sm cursor-pointer"
                    role="progressbar"
                    aria-valuenow={performance}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{ height: "6px" }}
                >
                    <div
                        className={`progress-bar bg-gradient-${bgColor}`}
                        style={{
                            width: hovered ? `${performance}%` : `${performance}%`,
                            transformOrigin: "left",
                            animation: hovered
                                ? `fillProgress ${performance / 100 + 2.5}s ease forwards`
                                : "none",
                        }}
                    ></div>
                </div>
                <div className="d-flex align-items-center justify-content-end mt-1">
                    {/* <small className="text-muted">{trend.text}</small> */}
                    <div className={`text-${statusColor} rounded-pill py-0 px-2 bg-opacity-7 bg-light-${statusColor} border border-${statusColor}`}>
                        <small className={`d-flex py-1 mx-1 fw-semibold justify-content-center align-items-center gap-2 text-${statusColor}`}>
                            {kpi.status}
                        </small>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

// Reusable Department Card Component
const DepartmentCard: React.FC<{ dept: DepartmentKpi }> = ({ dept }) => {
    const [hovered, setHovered] = useState(false);
    const IconComponent = dept.icon;

    return (
        <Card className={`rounded-4 mb-5 ${dept.bgColor} h-100 custom-card corner-${dept.color}`}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}>
            <CardBody className="w-100 p-6 h-100">
                <div className="d-flex align-items-start gap-2 mb-5">
                    <div className={`bg-gradient-${dept.color} p-3 rounded-3 card-icon-wrap`}>
                        <IconComponent className="text-white" size={18} />
                    </div>
                    <div>
                        <p className="h5 apple-title fw-bold mb-0">{dept.name}</p>
                        <small>{dept.description}</small>
                    </div>
                </div>
                <div>
                    {dept.kpis.map((kpi, index) => (
                        <div key={index} className={index > 0 ? 'my-3' : ''}>
                            <KPICard kpi={kpi} bgColor={dept.color} hovered={hovered} />
                        </div>
                    ))}
                </div>
                <hr className="text-muted mb-2" />
                <small className="text-muted">Source: {dept.source}</small>
            </CardBody>
        </Card>
    );
};

// Summary Stats Component with calculated averages
const SummaryStats: React.FC = () => {
    // Calculate average performance across all KPIs
    const allKpis = DEPARTMENTS.flatMap(dept => dept.kpis);
    const averagePerformance = Math.round(
        allKpis.reduce((acc, kpi) => acc + calculatePerformance(kpi.value, kpi.target), 0) / allKpis.length
    );

    // Calculate total KPIs
    const totalKpis = allKpis.length;

    // Count departments
    const totalDepartments = DEPARTMENTS.length;

    return (
        <div className="d-flex align-items-start gap-5">
            <div className="text-center">
                <p className="h1 apple-body text-success mb-0">{totalDepartments}</p>
                <small className="mb-0">Departments</small>
            </div>
            <div className="text-center">
                <p className="h1 apple-body text-primary mb-0">{totalKpis}</p>
                <small className="mb-0">KPIs Tracked</small>
            </div>
            <div className="text-center">
                <p className="h1 apple-body text-info mb-0">{averagePerformance}%</p>
                <small className="mb-0">Avg Performance</small>
            </div>
        </div>
    );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const KpiMonitoring: React.FC<Props> = ({ setShowCreateModal, filters, refreshData, setRefreshData }) => {
    const [loading, setLoading] = useState<boolean>(false);

    return (
        <>
            <div className="position-relative">
                {loading && <SimpleLoader className={"bg-black-50 z-2"} />}

                <SectionHeader
                    title="Department Performance Dashboard"
                    icon={LayoutDashboard}
                    description={(
                        <>
                            <p className="h4 apple-body text-muted fw-normal mb-3">
                                Department 5yr Strategic Plans
                            </p>
                            <p className="h6 apple-body text-muted mb-0">
                                Based on Annual Performance Plans 2024/25
                            </p>
                        </>
                    )}
                    action={<SummaryStats />}
                />

                {/* Department Cards Grid */}
                <Card className="rounded-4 mb-5">
                    <CardBody className="w-100 p-6">
                        <Row className="g-5">
                            {DEPARTMENTS.map((dept, index) => (
                                <Col sm={12} md={6} lg={4} key={index}>
                                    <DepartmentCard dept={dept} />
                                </Col>
                            ))}
                        </Row>
                    </CardBody>
                </Card>

                {/* Performance Highlights Card */}
                <Card className="rounded-4 mb-5">
                    <CardBody className="d-flex flex-column w-100 p-6">
                        <h6 className="fw-normal mb-10">Overall Performance Highlights</h6>
                        <div className="d-flex flex-wrap justify-content-around align-items-center gap-5">
                            {performanceData.map((item, index) => (
                                <div key={index} className="d-flex flex-column gap-1 align-items-center performance-item mt-0">
                                    <h1 className={`fw-normal mb-0 ${item.color}`}>
                                        {item.value}
                                    </h1>
                                    <small className="mb-0">{item.label}</small>
                                </div>
                            ))}
                        </div>
                    </CardBody>
                </Card>

                {/* <Card className="rounded-4 mb-5">
                    <CardBody className="d-flex flex-column w-100 p-6">
                        <h6 className="fw-normal mb-7">References</h6>
                        {references.map((item, index) => (
                            <div key={index} className={`d-flex justify-content-start align-items-center gap-2 ${index > 0 ? 'mt-2' : ''} reference-item`}>
                                <p className="mb-0"> | </p>
                                <small> {item} </small>
                            </div>
                        ))}
                    </CardBody>
                </Card> */}

                {/* <Card className="rounded-4 mb-5">
                    <CardBody className="row g-10 w-100 p-6 px-lg-20">
                        <div className="col-xl-3 col-sm-6 col-12 d-flex flex-column align-items-start gap-5 footer-item">
                            <div className={`d-flex justify-content-start align-items-center gap-3 `}>
                                <div className="rounded-circle bg-primary" style={{ width: '8px', height: '8px' }} />
                                <h6 className="fw-semibold mb-0"> Get In Touch</h6>
                            </div>
                            <div className="d-flex flex-column align-items-start mt-3 gap-7 ms-2">
                                <div className={`d-flex justify-content-start align-items-center gap-3 footer-subitem`}>
                                    <Mail size={14} className="text-primary" />
                                    <p className="fw-normal mb-0">invest@wcgov.za</p>
                                </div>
                                <div className={`d-flex justify-content-start align-items-center gap-3 footer-subitem`}>
                                    <Phone size={14} className="text-success" />
                                    <p className="fw-normal mb-0">+27 21 483 9999</p>
                                </div>
                                <div className={`d-flex justify-content-start align-items-center gap-3 footer-subitem`}>
                                    <Globe size={14} className="text-info" />
                                    <p className="fw-normal mb-0">wcif.wcgov.za</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 col-12  d-flex flex-column align-items-start gap-5 footer-item">
                            <div className={`d-flex justify-content-start align-items-center gap-3 `}>
                                <div className="rounded-circle bg-success" style={{ width: '8px', height: '8px' }} />
                                <h6 className="fw-semibold mb-0"> Investment Resources</h6>
                            </div>
                            <div className="d-flex flex-column align-items-start mt-3 gap-7 ms-2">
                                <div className={`d-flex justify-content-start align-items-center gap-3 footer-item`}>
                                    <Download size={14} className="text-dark fw-bold" />
                                    <p className="fw-bold mb-0">WCIF Strategy Document</p>
                                </div>
                                <div className={`d-flex justify-content-start align-items-center gap-3 footer-item`}>
                                    <Download size={14} className="text-dark fw-bold" />
                                    <p className="fw-bold mb-0">Investment Guidelines</p>
                                </div>
                                <div className={`d-flex justify-content-start align-items-center gap-3 footer-item`}>
                                    <Download size={14} className="text-dark fw-bold" />
                                    <p className="fw-bold mb-0">Due Diligence Checklist</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 col-12  d-flex flex-column align-items-start gap-5 footer-item">
                            <div className={`d-flex justify-content-start align-items-center gap-3 `}>
                                <div className="rounded-circle bg-info" style={{ width: '8px', height: '8px' }} />
                                <h6 className="fw-semibold mb-0">Key Contact</h6>
                            </div>
                            <div className="d-flex flex-column align-items-start mt-3 gap-7 ms-2">
                                <div className="footer-subitem">
                                    <p className="fs-6 fw-normal mb-1">Infrastructure Office</p>
                                    <p className="fs-6 fw-normal mb-0 text-muted">Project coordination</p>
                                </div>
                                <div className="footer-subitem">
                                    <p className="fs-6 fw-normal mb-1">Provincial Treasury</p>
                                    <p className="fs-6 fw-normal mb-0 text-muted">Financial structuring</p>
                                </div>
                                <div className="footer-subitem">
                                    <p className="fs-6 fw-normal mb-1">Transaction Advisory</p>
                                    <p className="fs-6 fw-normal mb-0 text-muted">Deal support</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 col-12  d-flex flex-column align-items-start gap-5 footer-item">
                            <div className={`d-flex justify-content-start align-items-center gap-3 `}>
                                <div className="rounded-circle bg-orange" style={{ width: '8px', height: '8px' }} />
                                <h6 className="fw-semibold mb-0"> Quick Actions</h6>
                            </div>
                            <div className="d-flex flex-column align-items-start mt-3 gap-7 w-100">
                                <div className="w-100 footer-item">
                                    <Button className="w-100 py-2 border-0 rounded-2 fw-bold text-white bg-primary">
                                        Express Investment Interest
                                    </Button>
                                </div>

                                <div className="w-100 footer-item">
                                    <Button variant="outline" className="w-100 py-2 fw-bold text-dark">
                                        Schedule Consultation
                                    </Button>
                                </div>

                                <div className="w-100 footer-item">
                                    <Button variant="outline" className="w-100 py-2 fw-bold text-dark">
                                        Request Information Pack
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card> */}
            </div>
        </>
    );
}

export { KpiMonitoring }